import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { QRBadge } from "./QRBadge";
import { IosInstallBanner } from "./IosInstallBanner";
import { refreshPushToken } from "../lib/push";
import { toast } from "sonner";
import {
    LayoutDashboard,
    Phone,
    MessageSquare,
    PhoneCall,
    CreditCard,
    Search,
    LogOut,
    User as UserIcon,
    Gift,
    Voicemail,
    Shield,
    ArrowRightLeft,
    Megaphone,
    Film,
    Signal,
    ShoppingBag,
    Wallet,
    LifeBuoy,
    Menu,
    X,
    Building2,
} from "lucide-react";

const navItems = [
    { to: "/dashboard", label: "My Account", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/mobile", label: "Plans & eSIM", icon: Signal, testid: "nav-mobile" },
    { to: "/numbers", label: "Get a number", icon: Search, testid: "nav-buy-number" },
    { to: "/my-numbers", label: "My numbers", icon: Phone, testid: "nav-my-numbers" },
    { to: "/porting", label: "Bring your number", icon: ArrowRightLeft, testid: "nav-porting" },
    { to: "/inbox", label: "Messages", icon: MessageSquare, testid: "nav-inbox" },
    { to: "/voicemails", label: "Voicemail", icon: Voicemail, testid: "nav-voicemails" },
    { to: "/shop", label: "Shop", icon: ShoppingBag, testid: "nav-shop" },
    { to: "/calls", label: "Backup calling", icon: PhoneCall, testid: "nav-calls" },
    { to: "/referrals", label: "Refer & earn", icon: Gift, testid: "nav-referrals" },
    { to: "/advertise", label: "Advertise", icon: Megaphone, testid: "nav-advertise" },
    { to: "/watch", label: "Watch", icon: Film, testid: "nav-watch" },
    { to: "/billing", label: "Plan & billing", icon: CreditCard, testid: "nav-billing" },
    { to: "/wallet", label: "Wallet", icon: Wallet, testid: "nav-wallet" },
    { to: "/support", label: "Support", icon: LifeBuoy, testid: "nav-support" },
    { to: "/profile", label: "My profile", icon: UserIcon, testid: "nav-profile" },
];

function Logo({ onClick }) {
    return (
        <img
            src="/flexline-wordmark.svg"
            alt="Flex Line"
            onClick={onClick}
            data-testid="sidebar-logo"
            className="h-8 w-auto cursor-pointer"
        />
    );
}

function NavLinks({ user, onNavigate }) {
    const [isDealer, setIsDealer] = useState(false);
    useEffect(() => {
        let active = true;
        api.get("/franchise/mine").then(({ data }) => { if (active) setIsDealer(!!data.is_dealer); }).catch(() => {});
        return () => { active = false; };
    }, []);
    return (
        <>
            {navItems.map(({ to, label, icon: Icon, testid }) => (
                <NavLink
                    key={to}
                    to={to}
                    data-testid={testid}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 text-sm rounded-sm transition-colors ${
                            isActive ? "bg-espresso text-[#FAF5FF]" : "text-zinc-700 hover:bg-cream"
                        }`
                    }
                >
                    <Icon className="w-4 h-4" />
                    {label}
                </NavLink>
            ))}
            {isDealer && (
                <NavLink
                    to="/franchise/portal"
                    data-testid="nav-dealer-portal"
                    onClick={onNavigate}
                    className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 text-sm rounded-sm transition-colors ${
                            isActive ? "bg-[#4C1D95] text-[#FAF5FF]" : "text-[#4C1D95] hover:bg-cream"
                        }`
                    }
                >
                    <Building2 className="w-4 h-4" />
                    Dealer Portal
                </NavLink>
            )}
            {user?.is_admin && (
                <NavLink
                    to="/admin"
                    data-testid="nav-admin"
                    onClick={onNavigate}
                    className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 text-sm rounded-sm transition-colors mt-3 border-t border-[#DDD6FE] pt-3 ${
                            isActive ? "bg-emerald-deep text-[#FAF5FF]" : "text-emerald-deep hover:bg-cream"
                        }`
                    }
                >
                    <Shield className="w-4 h-4" />
                    Admin
                </NavLink>
            )}
        </>
    );
}

function UserCard({ user, logout }) {
    return (
        <div className="border-t border-[#DDD6FE] p-3">
            <div className="flex items-center gap-3 px-2 py-2">
                {user?.picture ? (
                    <img
                        src={user.picture}
                        alt={user?.name}
                        className="w-8 h-8 rounded-sm object-cover border border-[#DDD6FE]"
                    />
                ) : (
                    <div className="w-8 h-8 rounded-sm bg-cream grid place-items-center">
                        <UserIcon className="w-4 h-4 text-zinc-500" />
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">
                        {user?.name}
                    </div>
                    <div className="text-[11px] text-zinc-500 truncate font-mono">{user?.email}</div>
                </div>
            </div>
            <button
                data-testid="logout-btn"
                onClick={logout}
                className="w-full mt-1 flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-cream rounded-sm"
            >
                <LogOut className="w-4 h-4" /> Sign out
            </button>
        </div>
    );
}

export default function AppShell({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);

    // Close mobile menu on route change
    React.useEffect(() => setMobileOpen(false), [location.pathname]);

    // Keep the device registered for push and ring/alert on any page (calls + texts)
    React.useEffect(() => {
        if (!user) return;
        const onPush = (payload) => {
            const data = payload?.data || {};
            const n = payload?.notification || {};
            if (data.type === "incoming_call") {
                toast(`Incoming call${data.from_number ? ` from ${data.from_number}` : ""}`, {
                    description: "Tap to open the dialer", duration: 15000,
                    action: { label: "Open", onClick: () => navigate("/dashboard") },
                });
                try { new Audio("/ringtone.mp3").play().catch(() => {}); } catch {}
            } else if (data.type === "new_text") {
                toast(n.title || "New text", {
                    description: n.body || "", duration: 8000,
                    action: { label: "Open", onClick: () => navigate("/inbox") },
                });
            }
        };
        refreshPushToken(onPush).catch(() => {});
    }, [user]);

    return (
        <div className="min-h-screen flex flex-col md:flex-row bg-cream">
            {/* Mobile top bar */}
            <header className="md:hidden sticky top-0 z-40 flex items-center justify-between px-4 py-3 bg-paper border-b border-[#DDD6FE]">
                <Logo onClick={() => navigate("/dashboard")} />
                <button
                    data-testid="mobile-menu-btn"
                    onClick={() => setMobileOpen((v) => !v)}
                    className="p-2 rounded-sm border border-[#DDD6FE] text-espresso"
                    aria-label="Toggle menu"
                >
                    {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </header>

            {/* Mobile drawer */}
            {mobileOpen && (
                <div className="md:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer">
                    <div className="w-72 max-w-[80vw] bg-paper h-full flex flex-col border-r border-[#DDD6FE]">
                        <div className="px-4 py-4 border-b border-[#DDD6FE] flex items-center justify-between">
                            <Logo onClick={() => navigate("/dashboard")} />
                            <button onClick={() => setMobileOpen(false)} className="p-1.5" aria-label="Close menu" data-testid="mobile-menu-close">
                                <X className="w-5 h-5 text-zinc-600" />
                            </button>
                        </div>
                        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
                            <NavLinks user={user} onNavigate={() => setMobileOpen(false)} />
                        </nav>
                        <div className="px-3 pb-3">
                            <QRBadge size={72} />
                        </div>
                        <UserCard user={user} logout={logout} />
                    </div>
                    <div className="flex-1 bg-black/40" onClick={() => setMobileOpen(false)} />
                </div>
            )}

            {/* Desktop sidebar */}
            <aside className="hidden md:flex w-64 border-r border-[#DDD6FE] flex-col bg-paper sticky top-0 h-screen">
                <div className="px-6 py-5 border-b border-[#DDD6FE]">
                    <Logo onClick={() => navigate("/dashboard")} />
                </div>
                <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
                    <NavLinks user={user} />
                </nav>
                <div className="px-3 pb-3">
                    <QRBadge size={72} />
                </div>
                <UserCard user={user} logout={logout} />
            </aside>

            {/* Main */}
            <main className="flex-1 min-w-0 bg-cream">
                <div className="px-4 py-6 sm:px-8 sm:py-8 max-w-7xl mx-auto fade-in">{children}</div>
            </main>
            <IosInstallBanner />
        </div>
    );
}
